import { itemDisplayName } from "../lib/off-menu";
import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useKitchenWebSocket } from "./useWebSocket";
import { useKitchenStore } from "../stores/kitchen.store";
import { useAudio } from "./useAudio";
import type {
  KitchenEvent,
  InventoryLowStockEventData,
  InventoryOutOfStockEventData,
  DishAvailabilityChangedEventData,
} from "@shared/types/events";

// Wave canonica per item: serviceWave e' il campo nuovo (post-fixing-courses),
// ma alcuni dati legacy hanno solo courseNumber. Usa il piu' specifico
// disponibile per evitare di collassare wave diverse a "1" via fallback.
function effectiveWave(item: any): number {
  const sw = item?.serviceWave;
  if (typeof sw === "number" && sw > 0) return sw;
  const cn = item?.courseNumber;
  if (typeof cn === "number" && cn > 0) return cn;
  return 1;
}

// True se l'ordine e' gia' stato lavorato/servito/cancellato e non c'e'
// niente per cui chiamare il cuoco. Usato per evitare che la voce annunci
// (o ricordi) comande gia' processate. Difese multiple per coprire stati
// stale dello store (race tra WS event e update DB):
//   - status order in {COMPLETED, CANCELLED, SERVED}
//   - completedAt o cancelledAt timestampati
//   - tutti gli items in {READY, SERVED, CANCELLED} oppure con servedAt set
function isOrderAlreadyDone(order: any): boolean {
  if (!order) return true;
  if (
    order.status === "COMPLETED" ||
    order.status === "CANCELLED" ||
    order.status === "SERVED"
  ) {
    return true;
  }
  if (order.completedAt || order.cancelledAt) return true;
  const items = (order.items ?? []) as Array<{
    status?: string;
    servedAt?: string | null;
  }>;
  if (items.length === 0) return false;
  return items.every(
    (i) =>
      Boolean(i?.servedAt) ||
      i?.status === "READY" ||
      i?.status === "SERVED" ||
      i?.status === "CANCELLED",
  );
}

// Regex categorie bevande — mirror di DRINKS_CATEGORY_REGEX in
// packages/table/lib/restaurant/category-classifier.ts. Copiata inline
// perche' il KDS web e' un bundle separato dal package table (stesso
// pattern dei mirror mobile/backend del classifier). Matcha il NOME della
// categoria del piatto (case-insensitive, prefix match con \b).
const DRINKS_CATEGORY_REGEX =
  /\b(cocktail|mocktail|aperitiv|distillat|liquor|liquore|amar(o|i)|spritz|drink|bevand|caff|té|tè|tea|bibit|birra|birre|acqua|water|vino|vini|bollic|gin|rum|vodka|whisky|whiskey|tequila|grappa|prosecc|champ|spuman|sake|cognac|brandy|mezcal|absinth|sambuc|limoncell|amaretto)/i;

// Bug fix 2026-05-22 + 2026-05-30: il chef voice menzionava le bevande
// (cocktail, spritz, bibite, birre, vino, acqua) nonostante vadano al BAR
// e non in cucina. La cucina non deve mai sentire annunci sulle bevande.
//
// Filtra fuori gli item che non sono di competenza cucina basandosi su DUE
// segnali:
//   1. dish.itemType ∈ {cocktail, wine, beverage, drink}
//   2. dish.category.name che matcha DRINKS_CATEGORY_REGEX (es. "Bibite",
//      "Birre", "Vini", "Acqua") — necessario perche' molte bevande hanno
//      itemType null/mancante ma sono classificate solo per categoria.
// Se MANCANO sia itemType sia category name, treat as kitchen item (safe
// default per non perdere annunci quando il payload e' incompleto).
function isKitchenItem(it: any): boolean {
  const t = it?.dish?.itemType;
  if (t) {
    const v = String(t).toLowerCase();
    // feat.107 — soft_drink/coffee/liqueur sono bevande → NON sono item di cucina.
    if (
      v === "cocktail" ||
      v === "wine" ||
      v === "beverage" ||
      v === "drink" ||
      v === "soft_drink" ||
      v === "coffee" ||
      v === "liqueur"
    ) {
      return false;
    }
  }
  const cat = it?.dish?.category?.name;
  if (cat && DRINKS_CATEGORY_REGEX.test(String(cat))) {
    return false;
  }
  return true;
}

// Aggrega gli items per (nome piatto + modifications + notes) e ritorna
// la lista formattata letta dalla voce KDS. Aggregazione per chiave composita
// cosi' 2 insalate "SENZA cipolla" diventano "2 insalata (SENZA cipolla)" e
// 1 insalata "SENZA olio" resta separata "1 insalata (SENZA olio)".
// modifications = stringa generata da chip "SENZA X" del cameriere.
// notes = testo libero del cameriere O nota scritta dal cliente dal menu QR.
function aggregateDishes(items: Array<any>, max: number = 6): string[] {
  const counts = new Map<
    string,
    { name: string; mods: string; count: number }
  >();
  for (const it of items) {
    // 248 — an off-menu line announces what the waiter wrote, not the anchor
    // dish: "Fuori menu, Fuori menu, Fuori menu" tells the kitchen nothing.
    const name = itemDisplayName(it, "");
    if (!name) continue;
    const modParts: string[] = [];
    if (it?.modifications && String(it.modifications).trim())
      modParts.push(String(it.modifications).trim());
    if (it?.notes && String(it.notes).trim())
      modParts.push(String(it.notes).trim());
    const mods = modParts.join(", ");
    const key = `${name}|${mods}`;
    const prev = counts.get(key) ?? { name, mods, count: 0 };
    prev.count += it?.quantity ?? 1;
    counts.set(key, prev);
  }
  const list: string[] = [];
  let i = 0;
  for (const g of counts.values()) {
    if (i >= max) break;
    const base = g.count > 1 ? `${g.count} ${g.name}` : g.name;
    list.push(g.mods ? `${base} (${g.mods})` : base);
    i++;
  }
  return list;
}

/**
 * How long a burst of events is gathered before the order list is re-read.
 *
 * Firing a course announces every dish on it, and a table being freed announces
 * every comanda on it. Each announcement used to pull the whole list down
 * again — the storm that wedged the kitchen display in September 2026. A third
 * of a second is below what anyone sees and turns a burst into one read.
 */
const ORDERS_REFRESH_COALESCE_MS = 300;

/**
 * Connects WebSocket events to React Query cache invalidation and Zustand store updates.
 * Mount this once at the app root level (e.g. ChefOverview).
 */
export function useKitchenRealtime(token: string | null) {
  const queryClient = useQueryClient();
  const { onEvent } = useKitchenWebSocket(token);

  // Re-read the order lists, once, however many events asked for it.
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshOrders = useCallback(() => {
    if (refreshTimer.current) return;
    refreshTimer.current = setTimeout(() => {
      refreshTimer.current = null;
      queryClient.invalidateQueries({ queryKey: ["kitchen-orders"] });
      queryClient.invalidateQueries({ queryKey: ["kitchen-station-orders"] });
    }, ORDERS_REFRESH_COALESCE_MS);
  }, [queryClient]);

  useEffect(
    () => () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    },
    [],
  );

  const { play, announce } = useAudio();
  const audioEnabled = useKitchenStore((s) => s.audioEnabled);
  const upsertOrder = useKitchenStore((s) => s.upsertOrder);
  const updateItemStatus = useKitchenStore((s) => s.updateItemStatus);
  const removeOrder = useKitchenStore((s) => s.removeOrder);
  const setDishStock = useKitchenStore((s) => s.setDishStock);
  const markWaveChange = useKitchenStore((s) => s.markWaveChange);
  const setFoodBlock = useKitchenStore((s) => s.setFoodBlock);

  useEffect(() => {
    if (!token) return;

    const unsubs: Array<() => void> = [];

    // New order → invalidate order lists, play sound, announce voice
    unsubs.push(
      onEvent("new_order", (event: KitchenEvent) => {
        refreshOrders();
        // Il backend puo' inviare il payload con due shape:
        //   {data: {order: {...}}} (legacy)
        //   {data: <order direct con tableNumber/tableName>} (post-feature 022)
        const data = event.data as any;
        const order = (data?.order ?? data) as any;
        if (order) {
          upsertOrder(order);
        }
        // Skip beep + voce se l'ordine e' arrivato gia' in stato terminale o
        // con tutti gli items gia' lavorati. Caso tipico: comanda riepilogo
        // creata dal cassiere e marcata SERVED quasi simultaneamente — il
        // cuoco non deve sentirsi annunciare una comanda gia' servita.
        if (isOrderAlreadyDone(order)) {
          // eslint-disable-next-line no-console
          console.log("[KdsVoice] new_order skipped (already done)", {
            orderId: order?.id,
            status: order?.status,
            itemStatuses: ((order?.items ?? []) as any[]).map(
              (i: any) => i?.status,
            ),
          });
          return;
        }
        if (audioEnabled) {
          play("newOrder");
          // Feature 022: voice announcement (silenzioso se cliente non ha kdsVoiceEnabled).
          const tableNumber = data?.tableNumber ?? order?.tableNumber ?? 0;
          const tableId = data?.tableId ?? order?.tableId ?? null;
          const dailyNumber = order?.dailyNumber ?? data?.dailyNumber ?? 0;
          // Estrai SOLO i piatti della INITIAL WAVE (la portata che parte
          // subito in cottura). Wave successive sono in coda — il chef non
          // deve sentirsele all'arrivo dell'ordine, le dira' la voce a fire-
          // course. Aggreghiamo per nome+quantita' cosi' "2 polpettoni" non
          // diventa "polpettone, polpettone".
          const orderItems = (order?.items ?? []) as Array<any>;
          // Bug fix 2026-05-22: filtra i bar items (cocktail/wine/
          // beverage/drink) — non sono di competenza cucina, il chef
          // non li deve sentire. Vanno al barman tramite BarOrdersScreen.
          const kitchenItems = orderItems.filter(isKitchenItem);
          if (kitchenItems.length === 0) {
            // eslint-disable-next-line no-console
            console.log("[KdsVoice] new_order skipped (bar-only order)", {
              orderId: order?.id,
              itemTypes: orderItems.map((i: any) => i?.dish?.itemType ?? "?"),
            });
            return;
          }
          const wavesPresent = kitchenItems.map(effectiveWave);
          const initialWave =
            wavesPresent.length > 0 ? Math.min(...wavesPresent) : 1;
          const initialItems = kitchenItems.filter(
            (it: any) => effectiveWave(it) === initialWave,
          );
          const dishes = aggregateDishes(initialItems, 6);
          // Nota a livello ordine: campo libero del cameriere/cliente QR
          // ("Tavolo allergie noci", "compleanno"). Lo passiamo separato dai
          // piatti cosi' la voce LLM puo' annunciarlo come informazione
          // contestuale alla fine, non come parte della lista piatti.
          const orderNote =
            typeof order?.notes === "string" && order.notes.trim()
              ? order.notes.trim()
              : null;
          announce("new_order", {
            tableNumber,
            dailyNumber,
            tableId,
            dishes,
            orderNote,
          }).catch(() => {});
        }
      }),
    );

    // Order updated → invalidate
    unsubs.push(
      onEvent("order_updated", (event: KitchenEvent) => {
        refreshOrders();
        if (event.data.order) {
          upsertOrder(event.data.order as any);
        }
      }),
    );

    // Item status changed → optimistic update in store, invalidate queries.
    // Robusto a entrambe le shape del payload:
    //   - { itemId, orderId, status, item? } (canonica per ItemStatusChangedEventData)
    //   - { id, orderId, status, ...item raw } (legacy: backend mandava l'item raw)
    // Senza questo fallback, prima del fix backend itemId era undefined e lo
    // store restava sempre stale → nag scanner credeva tutti gli items
    // ancora pendenti e annunciava reminder per comande gia' servite.
    unsubs.push(
      onEvent("item_status_changed", (event: KitchenEvent) => {
        const data = event.data as {
          itemId?: string;
          id?: string;
          status?: string;
        };
        const finalItemId = data?.itemId ?? data?.id;
        const status = data?.status;
        if (!finalItemId || !status) return;
        updateItemStatus(finalItemId, status);
        refreshOrders();
        if (status === "READY" && audioEnabled) play("itemReady");
      }),
    );

    // Feature 069 — Move Table Session: invalida tutte le query orders
    // così le card chef ridisegnano con la nuova etichetta tavolo
    // (`Tavolo 5` invece di `Sedia 30`). Nessuna voice announcement —
    // è un cambio di metadata, non di stato cucina.
    unsubs.push(
      onEvent("session_moved" as any, (_event: KitchenEvent) => {
        refreshOrders();
      }),
    );

    // Feature 190 — Order items moved between orders (correzione): invalidate
    // both source and destination so the "spostato da …" note appears live.
    // No sound / voice — it's a correction, not a new order.
    unsubs.push(
      onEvent("order_items_moved" as any, (_event: KitchenEvent) => {
        refreshOrders();
      }),
    );

    // Feature 109 — Waves reordered (spostamento ondate dal cameriere):
    // upsert ottimistico dell'ordine dal payload + invalidate + badge
    // temporaneo "spostato in Nª ondata" sulla card (FR-006a). NESSUN suono
    // né annuncio vocale (clarify Q3=B). Tollerante al payload legacy di
    // PUT /orders/:id/waves (data = order diretto, senza campi arricchiti).
    unsubs.push(
      onEvent("waves_reordered" as any, (event: KitchenEvent) => {
        refreshOrders();
        const data = event.data as any;
        const order = (data?.order ?? data) as any;
        if (order && order.id) {
          upsertOrder(order);
        }
        // Badge solo col payload arricchito (endpoint move-wave feature 109).
        const orderId: string | undefined = data?.orderId ?? order?.id;
        if (
          orderId &&
          Array.isArray(data?.movedItemIds) &&
          typeof data?.targetWave === "number"
        ) {
          markWaveChange(orderId, data.targetWave);
        }
      }),
    );

    // Course fired → invalidate + voice announcement
    unsubs.push(
      onEvent("course_fired", (event: KitchenEvent) => {
        refreshOrders();
        // Aggiorna SUBITO lo store con il nuovo firedAt sugli items della wave
        // appena firata, cosi' il nag scanner usa dati freschi (senza aspettare
        // il refetch react-query).
        const dataPre = event.data as any;
        const orderPre = (dataPre?.order ?? dataPre) as any;
        if (orderPre && orderPre.id) {
          upsertOrder(orderPre);
        }
        // Skip se ordine gia' globalmente terminato (impossibile in linea
        // teorica ma protegge da race condition fire+complete).
        if (isOrderAlreadyDone(orderPre)) {
          // eslint-disable-next-line no-console
          console.log("[KdsVoice] course_fired skipped (order already done)", {
            orderId: orderPre?.id,
            status: orderPre?.status,
          });
          return;
        }
        if (audioEnabled) {
          const data = event.data as any;
          const order = (data?.order ?? data) as any;
          const tableNumber = data?.tableNumber ?? order?.tableNumber ?? 0;
          const tableId = data?.tableId ?? order?.tableId ?? null;
          const courseNumber =
            data?.currentCourse ??
            order?.currentCourse ??
            data?.courseNumber ??
            1;
          const dailyNumber = order?.dailyNumber ?? data?.dailyNumber ?? 0;
          // Piatti della portata appena firata (= currentWave dell'order
          // dopo fireCourse). Filtra per effectiveWave + status non finito,
          // poi dedupe con quantita'.
          const orderItems = (order?.items ?? []) as Array<any>;
          const firedWave = (order?.currentWave ?? courseNumber) as number;
          // Bug fix 2026-05-22: filtra anche qui i bar items.
          const firedItems = orderItems.filter(
            (it: any) =>
              isKitchenItem(it) &&
              effectiveWave(it) === firedWave &&
              (!it?.status ||
                it.status === "FIRED" ||
                it.status === "PREPARING" ||
                it.status === "PENDING"),
          );
          // Skip se la wave appena firata non ha items pendenti (es. tutti
          // gli items della portata erano gia' READY/SERVED al momento del
          // fire — comanda lavorata in anticipo o duplicato di evento).
          if (firedItems.length === 0) {
            // eslint-disable-next-line no-console
            console.log(
              "[KdsVoice] course_fired skipped (no pending items in wave)",
              {
                orderId: order?.id,
                firedWave,
                itemStatuses: orderItems.map((i: any) => ({
                  status: i?.status,
                  ew: effectiveWave(i),
                })),
              },
            );
            return;
          }
          const dishes = aggregateDishes(firedItems, 6);
          const orderNote =
            typeof order?.notes === "string" && order.notes.trim()
              ? order.notes.trim()
              : null;
          announce("course_fired", {
            tableNumber,
            courseNumber,
            dailyNumber,
            tableId,
            dishes,
            orderNote,
          }).catch(() => {});
        }
      }),
    );

    // Voice config changed (T039: reset short-circuit immediato sul KDS)
    unsubs.push(
      onEvent("voice_config_changed" as any, () => {
        // Per ora: no state esplicito, il prossimo announce() rifara' fetch
        // del config dal backend tramite l'endpoint /tts/announce. Setup
        // completo per short-circuit-tracking sara' aggiunto se necessario.
      }),
    );

    // Nag overdue scanner — bucket in MINUTI reali (10/30/40). Quando un
    // ordine fired e non ancora servito raggiunge una delle soglie il KDS
    // genera un announce "nag_overdue".
    const NAG_TEST_MODE = false;
    const NAG_DIVISOR_MS = NAG_TEST_MODE ? 1000 : 60000; // sec o min
    const NAG_INTERVAL_MS = NAG_TEST_MODE ? 5000 : 60000; // scan freq
    const NAG_THRESHOLDS: Array<10 | 30 | 40> = [10, 30, 40];
    const TEST_DIVISOR = 4000; // unused in PROD (NAG_TEST_MODE=false)
    const triggered = new Set<string>();
    const nagInterval = window.setInterval(() => {
      if (!audioEnabled) return;
      const userName = useKitchenStore.getState().userName || "chef";
      // BUG FIX 2026-05-30 (voce annuncia tavoli gia' serviti): il nag scanner
      // leggeva da useKitchenStore.activeOrders (Zustand), aggiornato SOLO via
      // eventi WebSocket. Se il WS perdeva l'evento item_status_changed (es.
      // reconnect, sleep tab), lo store restava con gli item PENDING/PREPARING
      // e la voce annunciava ritardi su ordini in realta' gia' serviti.
      // Fix: leggiamo dalla cache React Query (["kitchen-orders", clientId,
      // undefined]) che fa polling autonomo ogni 5s ed e' la STESSA fonte che
      // alimenta le viste chef/stazione → sempre fresca. Fallback allo store
      // Zustand se la cache non e' ancora popolata (primo render).
      const nagClientId = useKitchenStore.getState().clientId;
      const freshOrders = nagClientId
        ? queryClient.getQueryData<any[]>([
            "kitchen-orders",
            nagClientId,
            undefined,
          ])
        : undefined;
      const orders =
        freshOrders && Array.isArray(freshOrders) && freshOrders.length >= 0
          ? freshOrders
          : useKitchenStore.getState().activeOrders;
      // eslint-disable-next-line no-console
      console.log("[KdsVoice] nag scan", {
        ordersCount: orders.length,
        source: freshOrders ? "react-query" : "zustand-fallback",
        audioEnabled,
        triggeredKeys: triggered.size,
      });
      const now = Date.now();
      for (const order of orders) {
        // Filtro robusto: copre status order terminale (COMPLETED/CANCELLED/
        // SERVED), completedAt/cancelledAt timestampati, e item-level done
        // (tutti READY/SERVED/CANCELLED). Risolve il caso "reminder per
        // comanda gia' servita": se per qualsiasi motivo lo store ha
        // un'istantanea piu' recente del backend (es. completedAt settato
        // ma status non ancora propagato) non scattiamo nag.
        if (isOrderAlreadyDone(order)) {
          // eslint-disable-next-line no-console
          console.log("[KdsVoice] nag scan skipped (order done)", {
            orderId: (order as any)?.id,
            status: (order as any)?.status,
          });
          continue;
        }
        // tableNumber puo' essere stringa "001"/"T1" o numero. Se mancante,
        // fallback al backend lookup via tableId (sempre presente sull'order).
        const tableNumber =
          (order as any).tableNumber ??
          (order as any).table?.tableNumber ??
          null;
        const tableId = (order as any).tableId ?? null;
        if (tableNumber === null && !tableId) continue;
        const items = ((order as any).items ?? []) as Array<{
          status?: string;
          servedAt?: string | null;
          completedAt?: string | null;
        }>;
        const hasPreparing = items.some((i) => i?.status === "PREPARING");
        const state: "PREPARING" | "PENDING" = hasPreparing
          ? "PREPARING"
          : "PENDING";
        const createdAt = (order as any).createdAt
          ? new Date((order as any).createdAt).getTime()
          : 0;
        // Wave attiva = wave PIU' BASSA (effectiveWave: serviceWave ||
        // courseNumber || 1) con almeno un item non finito. Usare
        // effectiveWave evita il bug "tutti default 1" quando i dati
        // legacy hanno serviceWave=null ma courseNumber valorizzato.
        // Defensive: anche se status non e' aggiornato (race con WS), un
        // item con servedAt o completedAt timestampato e' DONE — non lo
        // contiamo come "unfinished".
        const isItemUnfinished = (i: any): boolean => {
          if (i?.servedAt) return false;
          // Un piatto la cui ondata NON è stata firata (inviata in cucina dal
          // cameriere) NON è "in attesa di preparazione": la cucina non l'ha
          // ancora ricevuto. Niente sollecito vocale per questi (è normale che
          // restino fermi finché il cameriere non invia la portata).
          if (!i?.firedAt) return false;
          if (
            i?.status === "READY" ||
            i?.status === "SERVED" ||
            i?.status === "CANCELLED"
          ) {
            return false;
          }
          return (
            i?.status === "PENDING" ||
            i?.status === "PREPARING" ||
            i?.status === "FIRED" ||
            !i?.status
          );
        };
        const unfinishedWaves = items
          .filter(isItemUnfinished)
          .map((i: any) => effectiveWave(i));
        if (unfinishedWaves.length === 0) continue;
        const activeWave = Math.min(...unfinishedWaves);
        // referenceTime = MAX(firedAt) degli item della SOLA wave attiva.
        // Se nessuno item della wave attiva e' stato firato (es. ordine
        // appena creato senza fire), usa createdAt.
        const activeWaveFiredAts = items
          .filter((i: any) => {
            if (effectiveWave(i) !== activeWave) return false;
            return (
              i?.status === "PENDING" ||
              i?.status === "PREPARING" ||
              i?.status === "FIRED"
            );
          })
          .map((i: any) => i?.firedAt)
          .filter(Boolean)
          .map((s: string) => new Date(s).getTime());
        const referenceTime =
          activeWaveFiredAts.length > 0
            ? Math.max(...activeWaveFiredAts)
            : createdAt;
        if (!referenceTime) continue;
        const divisor = NAG_TEST_MODE ? TEST_DIVISOR : NAG_DIVISOR_MS;
        const elapsedUnits = Math.floor((now - referenceTime) / divisor);
        // eslint-disable-next-line no-console
        console.log("[KdsVoice] order check", {
          orderId: (order as any).id,
          state,
          activeWave,
          waveTime: new Date(referenceTime).toISOString(),
          activeFiredAtsCount: activeWaveFiredAts.length,
          elapsedUnits,
          itemsStatuses: items.map((i: any) => ({
            name: i?.dish?.name,
            status: i?.status,
            sw: i?.serviceWave,
            cn: i?.courseNumber,
            ew: effectiveWave(i),
            firedAt: i?.firedAt,
          })),
        });
        let bucket: 10 | 30 | 40 | null = null;
        for (const t of NAG_THRESHOLDS.slice().reverse()) {
          if (elapsedUnits >= t) {
            bucket = t;
            break;
          }
        }
        if (!bucket) continue;
        // Wave-aware key: la wave attiva e' identificata univocamente da
        // referenceTime (= MAX firedAt items attivi). Quando lo chef firma
        // una nuova portata, MAX(firedAt) cambia → chiave diversa → bucket
        // 5/10/15 ripartono per la nuova wave. NO state nella chiave perche'
        // PENDING/PREPARING flippano mentre il chef bumpa items, ma e' la
        // stessa wave.
        const key = `${(order as any).id}-w${referenceTime}-${bucket}`;
        if (triggered.has(key)) continue;
        triggered.add(key);
        // eslint-disable-next-line no-console
        console.log("[KdsVoice] nag triggered", {
          orderId: (order as any).id,
          tableNumber,
          state,
          bucket,
          elapsedUnits,
        });
        // pendingDishes = items della wave attiva con status non finito,
        // aggregati per nome+quantita' (no piatti duplicati nell'annuncio).
        // Bug fix 2026-05-30: filtra anche qui i bar items — il promemoria
        // ritardi non deve menzionare bevande (vanno al bar, non in cucina).
        const activeWaveItems = items.filter((i: any) => {
          if (!isKitchenItem(i)) return false;
          if (effectiveWave(i) !== activeWave) return false;
          return (
            i?.status === "PENDING" ||
            i?.status === "PREPARING" ||
            i?.status === "FIRED"
          );
        });
        // Se la wave attiva contiene SOLO bevande (nessun item di cucina),
        // non scatta alcun promemoria: la cucina non gestisce le bevande.
        if (activeWaveItems.length === 0) {
          // eslint-disable-next-line no-console
          console.log("[KdsVoice] nag skipped (bar-only wave)", {
            orderId: (order as any).id,
            activeWave,
          });
          continue;
        }
        const pendingDishes = aggregateDishes(activeWaveItems as any[], 4);
        announce("nag_overdue", {
          tableNumber: tableNumber ?? 0,
          tableId, // backend enrich tableNumber via DB lookup se mancante
          dailyNumber: (order as any).dailyNumber ?? null,
          minutes: bucket,
          state,
          chefName: userName,
          dishes: pendingDishes,
        }).catch(() => {});
      }
    }, NAG_INTERVAL_MS);

    unsubs.push(() => window.clearInterval(nagInterval));
    void NAG_DIVISOR_MS;

    // Order completed → remove from active, invalidate
    unsubs.push(
      onEvent("order_completed", (event: KitchenEvent) => {
        const { orderId } = event.data as { orderId: string };
        removeOrder(orderId);
        refreshOrders();
      }),
    );

    // Order cancelled
    // Feature 081 (T028b — C1 remediation): subscriber gia' presente.
    // `removeOrder(orderId)` rimuove l'intero order (+ tutti gli items) dalla
    // Zustand store activeOrders in modo ottimistico (<2s SC-004). Il
    // queryClient.invalidateQueries garantisce un refetch pulito dal backend
    // che esclude status='CANCELLED' (FR-010a: items spariscono dal KDS entro
    // 2s dalla cancellazione). Payload nuovo feature 081 include `itemIds[]`
    // ma non e' necessario usarli qui: rimuovere l'intero order e' piu' pulito
    // per il KDS (un ordine cancellato non ha piu' senso in nessuna stazione).
    unsubs.push(
      onEvent("order_cancelled", (event: KitchenEvent) => {
        const { orderId } = event.data as { orderId: string };
        removeOrder(orderId);
        refreshOrders();
        if (audioEnabled) play("alert");
      }),
    );

    // Feature 082 — Cancel Order Items (multi-select cancel).
    // Cancellazione parziale di alcuni items: l'ordine padre RESTA attivo.
    // Invalidiamo le query React Query così la prossima fetch riprende la
    // lista items filtrata server-side (status NOT IN CANCELLED), evitando
    // di lasciare gli items cancellati visibili sul KDS oltre 2s (SC-004).
    unsubs.push(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onEvent("order_items_cancelled" as any, (_event: KitchenEvent) => {
        refreshOrders();
        if (audioEnabled) play("alert");
      }),
    );

    // Feature 270 — the till re-priced a line (FR-011).
    //
    // The board redraws so the reason typed at the till — "porzione da 6" —
    // reaches the cook who has to plate six. That is the ONLY effect.
    //
    // Deliberately NO sound, NO voice, NO status change and NO alert: this is
    // an accounting correction on a line that may already be READY or SERVED,
    // and a chime here would send a cook back to a plate that went out twenty
    // minutes ago. `refreshOrders` invalidates; it never reopens anything —
    // the item statuses come back from the server exactly as they were.
    unsubs.push(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onEvent("order_item_price_changed" as any, (_event: KitchenEvent) => {
        refreshOrders();
      }),
    );

    // Inventory: dish running low
    unsubs.push(
      onEvent("inventory_low_stock", (event: KitchenEvent) => {
        const data = event.data as unknown as InventoryLowStockEventData;
        setDishStock(data.dishId, {
          isLowStock: true,
          isOutOfStock: false,
          currentStock: data.currentStock,
        });
      }),
    );

    // Inventory: dish out of stock
    unsubs.push(
      onEvent("inventory_out_of_stock", (event: KitchenEvent) => {
        const data = event.data as unknown as InventoryOutOfStockEventData;
        setDishStock(data.dishId, {
          isLowStock: false,
          isOutOfStock: true,
          currentStock: data.currentStock,
        });
      }),
    );

    // Inventory: dish availability changed (covers restocking too)
    unsubs.push(
      onEvent("dish_availability_changed", (event: KitchenEvent) => {
        const data = event.data as unknown as DishAvailabilityChangedEventData;
        if (data.inventory) {
          setDishStock(data.dishId, {
            isLowStock: data.inventory.isLowStock,
            isOutOfStock: data.inventory.isOutOfStock,
            currentStock: data.inventory.currentStock,
          });
        } else {
          // inventory: null means tracking was disabled or the record was removed —
          // clear stale stock state so badges don't get stuck.
          setDishStock(data.dishId, null);
        }
      }),
    );

    // Feature 113 — blocco ordini cibo: riflette lo stato su tutte le schermate
    // chef del locale (last-write-wins).
    unsubs.push(
      onEvent("food_block_changed" as any, (event: KitchenEvent) => {
        const data = event.data as unknown as {
          enabled?: boolean;
          active?: boolean;
        };
        setFoodBlock({
          enabled: !!data.enabled,
          active: !!data.active,
        });
      }),
    );

    return () => {
      unsubs.forEach((fn) => fn());
    };
  }, [
    token,
    audioEnabled,
    onEvent,
    queryClient,
    upsertOrder,
    updateItemStatus,
    removeOrder,
    setDishStock,
    markWaveChange,
    setFoodBlock,
    play,
  ]);
}
